import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDroppable, useDndMonitor } from '@dnd-kit/core';
import { motion } from 'motion/react';
import { CalendarDays, Archive, Star, Layers, Trash2, BookCheck, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, navActiveClass } from '@/lib/utils';
import { useTodoStore } from '@/stores/todoStore';
import { useTodoViewStore } from '@/stores/todoViewStore';
import { useTodoActionsStore } from '@/stores/todoActionsStore';
import { useUIStore } from '@/stores/uiStore';
import type { TodoPrimaryView, TodoList, TodoProject } from '@/types/todo';
import { getAnytimeTasks, getSomedayTasks, getTodayTasks, getUpcomingTasks } from '../utils/taskFilters';

// Smart-list identity colors. These were fixed hexes borrowed from Things;
// they now point at the theme's tone scale so the task sidebar recolors with
// the rest of the app instead of staying one saturated island. The mapping
// keeps each list's original meaning: Today warm, Upcoming urgent, Logbook done.
const viewButtons: Array<{ id: TodoPrimaryView; label: string; icon: React.ComponentType<{ className?: string }>; color?: string, fillColor?: string }> = [
  { id: 'inbox', label: 'Inbox', icon: Inbox, color: 'var(--tone-info)' },
  { id: 'today', label: 'Today', icon: Star, color: 'var(--tone-warning)', fillColor: 'var(--tone-warning)' },
  { id: 'upcoming', label: 'Upcoming', icon: CalendarDays, color: 'var(--tone-danger)' },
  { id: 'anytime', label: 'Anytime', icon: Layers, color: 'var(--tone-fresh)' },
  { id: 'someday', label: 'Someday', icon: Archive, color: 'var(--tone-caution)' },
  { id: 'logbook', label: 'Logbook', icon: BookCheck, color: 'var(--tone-success)' },
  { id: 'trash', label: 'Trash', icon: Trash2, color: 'var(--tone-neutral)' },
];

const droppableViewTargets = new Set<TodoPrimaryView>(['inbox', 'today', 'trash']);

const EMOJI_SEQUENCE =
  String.raw`(?:\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\u200D(?:\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?))*|\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3)`;
const SINGLE_EMOJI_REGEX = new RegExp(`^${EMOJI_SEQUENCE}$`, 'u');
const FIND_EMOJI_REGEX = new RegExp(EMOJI_SEQUENCE, 'u');

function normalizeEmojiInput(value: string): string {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (SINGLE_EMOJI_REGEX.test(trimmed)) {
    return trimmed;
  }
  const match = trimmed.match(FIND_EMOJI_REGEX);
  return match?.[0] ?? '';
}

function openSystemEmojiPicker(input: HTMLInputElement | null, options?: { ensureFocus?: boolean }) {
  if (!input) {
    return;
  }
  if (options?.ensureFocus && document.activeElement !== input) {
    input.focus();
  }
  input.select();
  if (typeof input.showPicker === 'function') {
    try {
      input.showPicker();
    } catch {
      // Ignore if the browser disallows programmatic picker opening.
    }
  }
}

export function TodoSidebarContent() {
  const activeView = useTodoViewStore((state) => state.activeView);
  const setActiveView = useTodoViewStore((state) => state.setActiveView);
  const activeListId = useTodoViewStore((state) => state.activeListId);
  const activeProjectId = useTodoViewStore((state) => state.activeProjectId);
  const listOrder = useTodoStore((state) => state.listOrder);
  const listsById = useTodoStore((state) => state.lists);
  const allLists = useMemo(() => listOrder.map((id) => listsById[id]).filter(Boolean), [listOrder, listsById]);
  const lists = useMemo(() => allLists.filter((list) => !list.isInbox), [allLists]);
  const inboxList = useMemo(() => allLists.find((list) => list.isInbox) ?? null, [allLists]);
  const projectsById = useTodoStore((state) => state.projects);
  const projects = useMemo(() => Object.values(projectsById), [projectsById]);
  const taskOrder = useTodoStore((state) => state.taskOrder);
  const tasksById = useTodoStore((state) => state.tasks);
  const tasks = useMemo(() => taskOrder.map((id) => tasksById[id]).filter(Boolean), [taskOrder, tasksById]);
  const taskCountByList = useMemo(() => {
    const counts: Record<string, number> = {};
    tasks.forEach((task) => {
      if (task?.status !== 'open') {
        return;
      }
      counts[task.listId] = (counts[task.listId] ?? 0) + 1;
    });
    return counts;
  }, [tasks]);
  const taskCountByProject = useMemo(() => {
    const counts: Record<string, number> = {};
    tasks.forEach((task) => {
      if (task?.status !== 'open' || !task.projectId) {
        return;
      }
      counts[task.projectId] = (counts[task.projectId] ?? 0) + 1;
    });
    return counts;
  }, [tasks]);
  const somedayHiddenCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    tasks.forEach((task) => {
      if (!task || task.status !== 'open' || !task.isSomeday) {
        return;
      }
      counts[task.listId] = (counts[task.listId] ?? 0) + 1;
    });
    return counts;
  }, [tasks]);
  const { createList, editList, destroyList, createProject, editProject, destroyProject } = useTodoActionsStore();
  const [listDialogState, setListDialogState] = useState<{ mode: 'create' | 'edit'; list: TodoList | null }>({
    mode: 'create',
    list: null,
  });
  const [projectDialogState, setProjectDialogState] = useState<{ mode: 'create' | 'edit'; project: TodoProject | null; listId: string | null }>({
    mode: 'create',
    project: null,
    listId: null,
  });
  const [isListDialogOpen, setIsListDialogOpen] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const setMobileMenuOpen = useUIStore((state) => state.setMobileMenuOpen);
  const closeSidebar = useCallback(() => setMobileMenuOpen(false), [setMobileMenuOpen]);

  const viewCounts = useMemo(() => {
    const inboxTasks =
      inboxList ? tasks.filter((task) => task?.listId === inboxList.id && task.status === 'open') : [];
    const today = getTodayTasks(tasks);
    const upcoming = getUpcomingTasks(tasks);
    const anytime = getAnytimeTasks(tasks);
    const someday = getSomedayTasks(tasks);
    const logbook = tasks.filter((task) => task.status === 'completed' || task.status === 'cancelled');
    const trash = tasks.filter((task) => task.status === 'deleted');
    return {
      inbox: inboxTasks.length,
      today: today.length,
      upcoming: upcoming.length,
      anytime: anytime.length,
      someday: someday.length,
      logbook: logbook.length,
      trash: trash.length,
    };
  }, [inboxList, tasks]);

  const [isDraggingTask, setIsDraggingTask] = useState(false);
  const [activeSidebarDropId, setActiveSidebarDropId] = useState<string | null>(null);
  const updateSidebarDropTarget = useCallback(
    (event: { active?: { id: unknown } | null; over?: { id: unknown } | null }) => {
      const activeId = event.active?.id;
      if (typeof activeId !== 'string' || !activeId.startsWith('task-')) {
        setActiveSidebarDropId(null);
        return;
      }
      const overId = event.over?.id;
      if (typeof overId === 'string' && overId.startsWith('sidebar-')) {
        setActiveSidebarDropId(overId);
      } else {
        setActiveSidebarDropId(null);
      }
    },
    [setActiveSidebarDropId]
  );

  useDndMonitor({
    onDragStart: (event) => {
      const activeId = event.active?.id;
      const draggingTask = typeof activeId === 'string' && activeId.startsWith('task-');
      setIsDraggingTask(draggingTask);
      if (!draggingTask) {
        setActiveSidebarDropId(null);
      }
    },
    onDragMove: updateSidebarDropTarget,
    onDragOver: updateSidebarDropTarget,
    onDragEnd: () => {
      setIsDraggingTask(false);
      setActiveSidebarDropId(null);
    },
    onDragCancel: () => {
      setIsDraggingTask(false);
      setActiveSidebarDropId(null);
    },
  });

  return (
    <div className="p-2 pt-2 md:pt-4 space-y-5">
      <div className="space-y-2">
        <div className="space-y-1">
          {viewButtons.map((view) => {
            const isActive = activeView === view.id;
            const count = viewCounts[view.id as keyof typeof viewCounts] ?? null;
            const canAcceptDrop = view.id === 'inbox' ? Boolean(inboxList) : true;
            return (
              <SidebarViewButton
                key={view.id}
                view={view}
                isActive={isActive}
                count={count}
                canAcceptDrop={canAcceptDrop}
                isDraggingTask={isDraggingTask}
                activeDropTargetId={activeSidebarDropId}
                onClick={() => {
                  setActiveView(view.id);
                  closeSidebar();
                }}
              />
            );
          })}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase text-sidebar-foreground/70">
          <span>Lists</span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs font-semibold text-sidebar-foreground/70 hover:text-foreground"
            onClick={() => {
              setListDialogState({ mode: 'create', list: null });
              setIsListDialogOpen(true);
            }}
          >
            + New
          </Button>
        </div>
        <div className="space-y-2">
          {lists.map((list) => (
            <SidebarListItem
              key={list.id}
              list={list}
              projects={projects.filter((project) => project.listId === list.id && project.status === 'active')}
              isActive={(activeView === 'anytime' || activeView === 'someday') && activeListId === list.id}
              taskCount={taskCountByList[list.id] ?? 0}
              somedayHiddenCount={somedayHiddenCounts[list.id] ?? 0}
              onSelect={(options) => {
                const targetView = activeView === 'someday' ? 'someday' : 'anytime';
                setActiveView(targetView, { listId: list.id, projectId: options?.projectId ?? null });
                closeSidebar();
              }}
              onEdit={() => {
                setListDialogState({ mode: 'edit', list });
                setIsListDialogOpen(true);
              }}
              onAddProject={() => {
                setProjectDialogState({ mode: 'create', project: null, listId: list.id });
                setIsProjectDialogOpen(true);
              }}
              dragId={`sidebar-list-${list.id}`}
              isDraggingTask={isDraggingTask}
              activeDropTargetId={activeSidebarDropId}
              activeProjectId={activeProjectId}
              activeView={activeView}
              onEditProject={(project) => {
                setProjectDialogState({ mode: 'edit', project, listId: project.listId });
                setIsProjectDialogOpen(true);
              }}
              onCompleteProject={async (project) => {
                await editProject(project.id, { status: 'completed' });
              }}
              onDeleteProject={async (project) => {
                await destroyProject(project.id);
              }}
              projectTaskCountMap={taskCountByProject}
            />
          ))}
        </div>
      </div>
      <ListDialog
        open={isListDialogOpen}
        mode={listDialogState.mode}
        list={listDialogState.list}
        onClose={() => setIsListDialogOpen(false)}
        onSubmit={async (data) => {
          if (listDialogState.mode === 'create') {
            await createList(data);
          } else if (listDialogState.list) {
            await editList(listDialogState.list.id, data);
          }
          setIsListDialogOpen(false);
        }}
        onDelete={
          listDialogState.mode === 'edit' && listDialogState.list
            ? async () => {
                await destroyList(listDialogState.list!.id);
                setIsListDialogOpen(false);
              }
            : undefined
        }
      />
      <ProjectDialog
        open={isProjectDialogOpen}
        mode={projectDialogState.mode}
        project={projectDialogState.project}
        listId={projectDialogState.listId}
        lists={lists}
        onClose={() => setIsProjectDialogOpen(false)}
        onSubmit={async (data) => {
          if (projectDialogState.mode === 'create') {
            await createProject(data);
          } else if (projectDialogState.project) {
            await editProject(projectDialogState.project.id, data);
          }
          setIsProjectDialogOpen(false);
        }}
        onDelete={
          projectDialogState.mode === 'edit' && projectDialogState.project
            ? async () => {
                await destroyProject(projectDialogState.project!.id);
                setIsProjectDialogOpen(false);
              }
            : undefined
        }
      />
    </div>
  );
}

interface SidebarViewButtonProps {
  view: (typeof viewButtons)[number];
  isActive: boolean;
  count: number | null;
  onClick: () => void;
  isDraggingTask: boolean;
  activeDropTargetId: string | null;
  canAcceptDrop?: boolean;
}

function SidebarViewButton({
  view,
  isActive,
  count,
  onClick,
  isDraggingTask,
  activeDropTargetId,
  canAcceptDrop = true,
}: SidebarViewButtonProps) {
  const droppableId = `sidebar-view-${view.id}`;
  const isDropTarget = droppableViewTargets.has(view.id) && canAcceptDrop;
  const { setNodeRef, isOver } = useDroppable({ id: droppableId, disabled: !isDropTarget });
  const dropActive = isDropTarget && isDraggingTask && (isOver || activeDropTargetId === droppableId);
  const dropArmed = isDropTarget && isDraggingTask && !dropActive;
  const Icon = view.icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
  const showCount = typeof count === 'number' && count > 0;

  return (
    <motion.div
      ref={isDropTarget ? setNodeRef : undefined}
      initial={false}
      animate={dropActive ? { scale: 1.02 } : { scale: 1 }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
      className={cn(
        'rounded-2xl transition-all',
        dropActive && 'bg-primary/5 ring-2 ring-primary/60 shadow-lg',
        dropArmed && 'ring-1 ring-primary/30'
      )}
    >
      <Button
        type="button"
        variant={isActive ? 'secondary' : 'ghost'}
        className={cn(
          'w-full justify-between gap-3 rounded-full px-3 py-2 text-sm font-normal transition-all duration-200',
          isActive ? navActiveClass : 'text-muted-foreground hover:text-foreground',
          dropActive && 'ring-2 ring-primary bg-primary/10 text-foreground shadow-lg',
          dropArmed && 'ring-1 ring-primary/30'
        )}
        onClick={onClick}
      >
        <span className="flex items-center gap-3">
          <Icon className="h-4 w-4" style={{ color: view.color ?? undefined, fill: view.fillColor ?? undefined }} />
          <span className="truncate">{view.label}</span>
        </span>
        {showCount ? <Badge variant={isActive ? 'default' : 'secondary'}>{count}</Badge> : null}
      </Button>
    </motion.div>
  );
}

interface SidebarListItemProps {
  list: TodoList;
  projects: TodoProject[];
  isActive: boolean;
  taskCount: number;
  somedayHiddenCount: number;
  onSelect: (options?: { projectId?: string | null }) => void;
  onEdit: () => void;
  onAddProject: () => void;
  dragId: string;
  isDraggingTask: boolean;
  activeDropTargetId: string | null;
  activeProjectId: string | null;
  activeView: TodoPrimaryView;
  onEditProject: (project: TodoProject) => void | Promise<void>;
  onCompleteProject: (project: TodoProject) => void | Promise<void>;
  onDeleteProject: (project: TodoProject) => void | Promise<void>;
  projectTaskCountMap: Record<string, number>;
}

function SidebarListItem({
  list,
  projects,
  isActive,
  taskCount,
  onSelect,
  onEdit,
  onAddProject,
  dragId,
  isDraggingTask,
  activeDropTargetId,
  activeProjectId,
  activeView,
  onEditProject,
  onCompleteProject,
  onDeleteProject,
  projectTaskCountMap,
}: SidebarListItemProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: dragId,
  });
  const dropActive = isDraggingTask && (isOver || activeDropTargetId === dragId);
  const dropArmed = isDraggingTask && !dropActive;

  return (
    <motion.div
      ref={setNodeRef}
      initial={false}
      animate={dropActive ? { scale: 1.03 } : { scale: 1 }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
      className={cn(
        'rounded-2xl p-1 transition-all',
        dropActive && 'bg-primary/5 ring-2 ring-primary/60 shadow-lg',
        dropArmed && 'ring-1 ring-primary/30'
      )}
    >
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <Button
            type="button"
            variant={isActive ? 'secondary' : 'ghost'}
            className={cn(
              'w-full justify-between gap-3 rounded-full px-3 py-2 text-left text-sm font-normal transition-all duration-200',
              isActive
                ? navActiveClass
                : 'text-muted-foreground hover:text-foreground',
              dropActive && 'ring-2 ring-primary bg-primary/10 text-foreground shadow-lg',
              dropArmed && 'ring-1 ring-primary/30'
            )}
            onClick={() => onSelect()}
          >
            <span className="flex items-center gap-2 truncate">
              {list.icon ? <span className="text-base">{list.icon}</span> : null}
              <span className="truncate">{list.name}</span>
            </span>
            {taskCount > 0 ? <Badge variant={isActive ? 'default' : 'secondary'}>{taskCount}</Badge> : null}
          </Button>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <ContextMenuItem onSelect={onAddProject}>Add Project</ContextMenuItem>
          {list.isInbox ? (
            <ContextMenuItem disabled className="text-muted-foreground">
              Inbox locked
            </ContextMenuItem>
          ) : (
            <ContextMenuItem onSelect={onEdit}>Edit List</ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {projects.length > 0 && (
        <div className="ml-0 mt-2 space-y-1 pl-3">
          {projects.map((project) => (
            <SidebarProjectItem
              key={project.id}
              project={project}
              isActive={(activeView === 'anytime' || activeView === 'someday') && activeProjectId === project.id}
              onSelect={() => onSelect({ projectId: project.id })}
              taskCount={projectTaskCountMap[project.id] ?? 0}
              onEdit={() => onEditProject(project)}
              onComplete={() => onCompleteProject(project)}
              onDelete={() => onDeleteProject(project)}
              isDraggingTask={isDraggingTask}
              activeDropTargetId={activeDropTargetId}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

interface SidebarProjectItemProps {
  project: TodoProject;
  onSelect: () => void;
  isActive: boolean;
  taskCount: number;
  onEdit: () => void | Promise<void>;
  onComplete: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  isDraggingTask: boolean;
  activeDropTargetId: string | null;
}

function SidebarProjectItem({
  project,
  onSelect,
  isActive,
  taskCount,
  onEdit,
  onComplete,
  onDelete,
  isDraggingTask,
  activeDropTargetId,
}: SidebarProjectItemProps) {
  const droppableId = `sidebar-project-${project.id}`;
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  const dropActive = isDraggingTask && (isOver || activeDropTargetId === droppableId);
  const dropArmed = isDraggingTask && !dropActive;
  return (
    <motion.div
      ref={setNodeRef}
      initial={false}
      animate={dropActive ? { x: 4, scale: 1.02 } : { x: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
      className={cn(
        'rounded-full px-1 py-0.5 transition-all',
        dropActive && 'bg-primary/5 ring-2 ring-primary/60 shadow-lg',
        dropArmed && 'ring-1 ring-primary/30'
      )}
    >
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <Button
            type="button"
            variant={isActive ? 'secondary' : 'ghost'}
            onClick={onSelect}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-full px-3 py-1.5 text-left text-sm font-normal transition-all duration-200',
              isActive
                ? navActiveClass
                : 'text-muted-foreground hover:text-foreground',
              dropActive && 'ring-2 ring-primary bg-primary/10 text-foreground shadow-lg',
              dropArmed && 'ring-1 ring-primary/30'
            )}
          >
            <span className="flex items-center gap-2 truncate">
              {project.icon ? (
                <span className="text-base leading-none">{project.icon}</span>
              ) : project.color ? (
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: project.color ?? undefined }}
                />
              ) : null}
              <span className="truncate">{project.name}</span>
            </span>
            {taskCount > 0 ? <Badge variant={isActive ? 'default' : 'secondary'}>{taskCount}</Badge> : null}
          </Button>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <ContextMenuItem onSelect={onEdit}>Edit Project</ContextMenuItem>
          <ContextMenuItem onSelect={onComplete}>Mark Complete</ContextMenuItem>
          <ContextMenuItem variant="destructive" onSelect={onDelete}>
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </motion.div>
  );
}

interface ListDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  list: TodoList | null;
  onClose: () => void;
  onSubmit: (data: { name: string; icon?: string | null; color?: string | null }) => void;
  onDelete?: () => void;
}

function ListDialog({ open, mode, list, onClose, onSubmit, onDelete }: ListDialogProps) {
  const [name, setName] = useState(list?.name ?? '');
  const [icon, setIcon] = useState(list?.icon ? normalizeEmojiInput(list.icon) : '');
  const [color, setColor] = useState(list?.color ?? '#a5b4fc');
  const iconInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setName(list?.name ?? '');
    setIcon(list?.icon ? normalizeEmojiInput(list.icon) : '');
    setColor(list?.color ?? '#a5b4fc');
  }, [list, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New List' : 'Edit List'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="list-name">Name</Label>
            <Input id="list-name" value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label htmlFor="list-icon">Icon</Label>
              <Input
                id="list-icon"
                ref={iconInputRef}
                value={icon}
                onChange={(event) => setIcon(normalizeEmojiInput(event.target.value))}
                onClick={() => openSystemEmojiPicker(iconInputRef.current, { ensureFocus: true })}
                onFocus={() => openSystemEmojiPicker(iconInputRef.current)}
                placeholder="✨"
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="list-color">Color</Label>
              <Input
                id="list-color"
                type="color"
                value={color || '#a5b4fc'}
                onChange={(event) => setColor(event.target.value)}
                className="h-10"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="flex flex-row justify-between">
          {onDelete ? (
            <Button variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!name.trim()) {
                  return;
                }
                const normalizedIcon = normalizeEmojiInput(icon);
                onSubmit({ name: name.trim(), icon: normalizedIcon || null, color });
              }}
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ProjectDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  listId: string | null;
  project: TodoProject | null;
  lists: TodoList[];
  onClose: () => void;
  onSubmit: (data: { listId: string; name: string; icon?: string | null; color?: string | null }) => void;
  onDelete?: () => void;
}

function ProjectDialog({ open, mode, listId, project, lists, onClose, onSubmit, onDelete }: ProjectDialogProps) {
  const [name, setName] = useState(project?.name ?? '');
  const [selectedListId, setSelectedListId] = useState<string | null>(listId ?? project?.listId ?? (lists[0]?.id ?? null));
  const [icon, setIcon] = useState(project?.icon ? normalizeEmojiInput(project.icon) : '');
  const [color, setColor] = useState(project?.color ?? '#a5b4fc');
  const iconInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setName(project?.name ?? '');
    setSelectedListId(listId ?? project?.listId ?? (lists[0]?.id ?? null));
    setIcon(project?.icon ? normalizeEmojiInput(project.icon) : '');
    setColor(project?.color ?? '#a5b4fc');
  }, [listId, lists, open, project]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New Project' : 'Edit Project'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="project-name">Name</Label>
            <Input id="project-name" value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label htmlFor="project-icon">Icon</Label>
              <Input
                id="project-icon"
                ref={iconInputRef}
                value={icon}
                onChange={(event) => setIcon(normalizeEmojiInput(event.target.value))}
                onClick={() => openSystemEmojiPicker(iconInputRef.current, { ensureFocus: true })}
                onFocus={() => openSystemEmojiPicker(iconInputRef.current)}
                placeholder="📁"
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="project-color">Color</Label>
              <Input
                id="project-color"
                type="color"
                value={color || '#a5b4fc'}
                onChange={(event) => setColor(event.target.value)}
                className="h-10"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="project-list">List</Label>
            <Select value={selectedListId ?? ''} onValueChange={(value) => setSelectedListId(value)}>
              <SelectTrigger id="project-list">
                <SelectValue placeholder="Choose list" />
              </SelectTrigger>
              <SelectContent>
                {lists.map((entry) => (
                  <SelectItem key={entry.id} value={entry.id}>
                    {entry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex flex-row justify-between">
          {onDelete ? (
            <Button variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!name.trim() || !selectedListId) {
                  return;
                }
                const normalizedIcon = normalizeEmojiInput(icon);
                onSubmit({ listId: selectedListId, name: name.trim(), icon: normalizedIcon || null, color });
              }}
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
